import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as intlTelInput from 'intl-tel-input';
import { TEL_COUNTRIES } from 'src/utils/constants';
import { IPhoneInfo } from 'src/utils/declarations';

declare const intlTelInputUtils: any;

const IntlTelInputConfig: intlTelInput.Options = {
  utilsScript: 'assets/scripts/intlTelInput.js',
  nationalMode: false,
  formatOnDisplay: true,
  initialCountry: 'ua',
  onlyCountries: TEL_COUNTRIES,
  preferredCountries: ['ua'],
  autoPlaceholder: 'aggressive',
  // autoHideDialCode: false,
};

@Component({
  selector: 'ngx-intl-tel-input',
  templateUrl: './intlTelInput.component.html',
  styleUrls: ['./intlTelInput.component.scss'],
})
export class IntlTelInputComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('telInput') telInput: any;
  @Input() phoneNumber = '';
  @Input() disabled = false;
  @Output() phoneInfoChange = new EventEmitter<IPhoneInfo>();
  iti: any;
  isInvalid = false;
  isDirty = false;
  selectedCountryCode: number;
  countryData: intlTelInput.CountryData[];
  errorMessage: string;
  errorCode: number;

  ngOnInit() {}

  ngAfterViewInit() {
    this.countryData = window.intlTelInputGlobals.getCountryData();

    this.changeCountriesName();
    this.iti = intlTelInput(this.telInput.nativeElement, IntlTelInputConfig);
    this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode;
    this.telInput.nativeElement.addEventListener(
      'countrychange',
      this.onChangeDialCode
    );
  }

  ngOnChanges() {
    this.translate.onLangChange.subscribe(() => {
      this.iti.destroy();

      this.changeCountriesName();

      this.iti = intlTelInput(this.telInput.nativeElement, IntlTelInputConfig);

      this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode;
      this.errorMessage = this.translate.instant(`VALIDATION.COMMON.PHONE`)[
        this.errorCode
      ];
    });

    // on meetings page is inserting phone value and this condition for clear error message after selecting
    if (this.phoneNumber?.length === 13 && this.errorCode) {
      this.iti.setNumber(this.phoneNumber);
      this.onPhoneNumberChange();
    }
  }

  ngOnDestroy() {
    this.iti.destroy();
    this.telInput.nativeElement.removeEventListener(
      'countrychange',
      this.onChangeDialCode
    );
  }

  onFocus = () => {
    if (!this.phoneNumber) {
      const getCode = this.iti.getSelectedCountryData().dialCode;
      this.phoneNumber = '+' + getCode;
    }
  };

  onInputKeyPress = (event: KeyboardEvent) => {
    const allowedChars = /[0-9\+\ ]/;
    const allowedCtrlChars = /[axcv]/; // Allows copy-pasting
    const allowedOtherKeys = [
      'ArrowLeft',
      'ArrowUp',
      'ArrowRight',
      'ArrowDown',
      'Home',
      'End',
      'Insert',
      'Delete',
      'Backspace',
    ];

    if (
      !allowedChars.test(event.key) &&
      !(event.ctrlKey && allowedCtrlChars.test(event.key)) &&
      !allowedOtherKeys.includes(event.key)
    ) {
      event.preventDefault();
    }
  };

  formatIntlTelInput() {
    if (typeof intlTelInputUtils !== 'undefined') {
      // utils are lazy loaded, so must check
      const currentText = this.iti.getNumber(
        intlTelInputUtils.numberFormat.E164
      );
      if (typeof currentText === 'string') {
        // sometimes the currentText is an object :)
        this.iti.setNumber(currentText); // will autoformat because of formatOnDisplay=true
      }
    }
  }

  onPhoneNumberChange = () => {
    this.isInvalid = false;
    this.isDirty = true;
    if (this.phoneNumber && this.phoneNumber.trim()) {
      if (!this.iti.isValidNumber()) {
        this.isInvalid = true;
        this.errorCode = this.iti.getValidationError(
          this.phoneNumber,
          this.selectedCountryCode
        );
        this.errorMessage = this.translate.instant(`VALIDATION.COMMON.PHONE`)[
          this.errorCode
        ];
      }
    }

    this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode;

    this.sendPhoneInfoToParentComponent({
      value: this.phoneNumber,
      code: this.selectedCountryCode,
      isInvalid: this.isInvalid,
      isDirty: this.isDirty,
    });
  };

  onChangeDialCode = () => {
    this.onPhoneNumberChange();
  };

  sendPhoneInfoToParentComponent = (params: IPhoneInfo) => {
    this.phoneInfoChange.emit(params);
  };

  changeCountriesName = () => {
    for (let i = 0; i < this.countryData.length; i++) {
      if (TEL_COUNTRIES.includes(this.countryData[i].iso2)) {
        const country = this.countryData[i];

        country.name = this.translate.instant(`COUNTRIES.${country.iso2}`);
      }
    }
  };

  constructor(private translate: TranslateService) {}
}
